import React from 'react';
import { Check, Loader2} from 'lucide-react';

const PricingPage = ({ user, handleGoogleLogin, handlePurchaseCredits, loading, isLoadingCheckout }) => {
  return (
    <div className="max-w-5xl mx-auto">
      <div className="text-center mb-12">
        <h2 className="text-4xl font-bold text-gray-900 mb-4">Simple, Transparent Pricing</h2>
        <p className="text-xl text-gray-600">No subscriptions. No hidden fees. Pay only for what you use.</p>
      </div>

      {/* Free Tier */}
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl p-8 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-2xl font-bold text-gray-900">Free Trial</h3>
            <p className="text-gray-600">Perfect for testing the system</p>
          </div>
          <div className="text-right">
            <div className="text-4xl font-bold text-green-600">$0</div>
            <div className="text-sm text-gray-600">10 free invoices</div>
          </div>
        </div>
        <ul className="space-y-2 text-gray-700">
          <li className="flex items-center gap-2">
            <Check className="w-5 h-5 text-green-600" />
            <span>10 invoice processing credits</span>
          </li>
          <li className="flex items-center gap-2">
            <Check className="w-5 h-5 text-green-600" />
            <span>No credit card required</span>
          </li>
          <li className="flex items-center gap-2">
            <Check className="w-5 h-5 text-green-600" />
            <span>Full feature access</span>
          </li>
          <li className="flex items-center gap-2">
            <Check className="w-5 h-5 text-green-600" />
            <span>CSV & JSON export</span>
          </li>
        </ul>
      </div>

      {/* Pricing Tiers */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Starter Pack */}
        <div className="bg-white border-2 border-indigo-200 rounded-2xl p-6 hover:shadow-xl transition-shadow">
          <div className="text-center mb-4">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Starter</h3>
            <div className="text-4xl font-bold text-indigo-600 mb-1">$5</div>
            <div className="text-sm text-gray-600">30 invoices</div>
            <div className="text-xs text-gray-500 mt-1">$0.17 per invoice</div>
          </div>
          <ul className="space-y-2 text-sm text-gray-700 mb-6">
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-indigo-600" />
              <span>30 processing credits</span>
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-indigo-600" />
              <span>Credits never expire</span>
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-indigo-600" />
              <span>Instant delivery</span>
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-indigo-600" />
              <span>Perfect for small teams</span>
            </li>
          </ul>
          <button 
            onClick={() => {
              if (!user) {
                handleGoogleLogin();
              } else {
                handlePurchaseCredits(30, 5.00);
              }
            }}
            disabled={loading || isLoadingCheckout}
            className="w-full py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition disabled:opacity-50"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin mx-auto" />
            ) : user ? (
              'Purchase Now'
            ) : (
              'Sign In to Purchase'
            )}
          </button>
        </div>

        {/* Professional Pack - Most Popular */}
        <div className="bg-white border-2 border-purple-400 rounded-2xl p-6 hover:shadow-xl transition-shadow relative">
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
            <span className="px-4 py-1 bg-purple-600 text-white text-xs font-bold rounded-full">
              ⭐ MOST POPULAR
            </span>
          </div>
          <div className="text-center mb-4 mt-2">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Professional</h3>
            <div className="text-4xl font-bold text-purple-600 mb-1">$10</div>
            <div className="text-sm text-gray-600">100 invoices</div>
            <div className="text-xs text-gray-500 mt-1">$0.10 per invoice</div>
            <div className="text-xs text-green-600 font-semibold mt-1">Save 41%</div>
          </div>
          <ul className="space-y-2 text-sm text-gray-700 mb-6">
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-purple-600" />
              <span>100 processing credits</span>
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-purple-600" />
              <span>Credits never expire</span>
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-purple-600" />
              <span>Best for growing businesses</span>
            </li>
          </ul>
          <button 
            onClick={() => {
              if (!user) {
                handleGoogleLogin();
              } else {
                handlePurchaseCredits(100, 10.00);
              }
            }}
            disabled={loading || isLoadingCheckout}
            className="w-full py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition disabled:opacity-50"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin mx-auto" />
            ) : user ? (
              'Purchase Now'
            ) : (
              'Sign In to Purchase'
            )}
          </button>
        </div>

        {/* Business Pack - Best Value */}
        <div className="bg-white border-2 border-amber-300 rounded-2xl p-6 hover:shadow-xl transition-shadow relative">
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
            <span className="px-4 py-1 bg-amber-600 text-white text-xs font-bold rounded-full">
              💰 BEST VALUE
            </span>
          </div>
          <div className="text-center mb-4 mt-2">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Business</h3>
            <div className="text-4xl font-bold text-amber-600 mb-1">$30</div>
            <div className="text-sm text-gray-600">310 invoices</div>
            <div className="text-xs text-gray-500 mt-1">$0.097 per invoice</div>
            <div className="text-xs text-green-600 font-semibold mt-1">Save 53%</div>
          </div>
          <ul className="space-y-2 text-sm text-gray-700 mb-6">
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-amber-600" />
              <span>310 processing credits</span>
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-amber-600" />
              <span>Credits never expire</span>
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-amber-600" />
              <span>High-volume processing</span>
            </li>
          </ul>
          <button 
            onClick={() => {
              if (!user) {
                handleGoogleLogin();
              } else {
                handlePurchaseCredits(310, 30.00);
              }
            }}
            disabled={loading || isLoadingCheckout}
            className="w-full py-3 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 transition disabled:opacity-50"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin mx-auto" />
            ) : user ? (
              'Purchase Now'
            ) : (
              'Sign In to Purchase'
            )}
          </button>
        </div>
      </div>

      {/* Features Section */}
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-8 border border-indigo-200">
        <h3 className="text-2xl font-bold text-center text-gray-900 mb-6">All Plans Include</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start gap-3">
            <Check className="w-5 h-5 text-indigo-600 mt-0.5" />
            <div>
              <p className="font-semibold text-gray-900">AI-Powered Extraction</p>
              <p className="text-sm text-gray-600">AI processes your invoices with industry-leading accuracy</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Check className="w-5 h-5 text-indigo-600 mt-0.5" />
            <div>
              <p className="font-semibold text-gray-900">Multi-System Compatible</p>
              <p className="text-sm text-gray-600">Export to CSV and JSON formats for QuickBooks & Coupa</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Check className="w-5 h-5 text-indigo-600 mt-0.5" />
            <div>
              <p className="font-semibold text-gray-900">Secure Processing</p>
              <p className="text-sm text-gray-600">Bank-level encryption and secure data handling</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Check className="w-5 h-5 text-indigo-600 mt-0.5" />
            <div>
              <p className="font-semibold text-gray-900">No Expiration</p>
              <p className="text-sm text-gray-600">Your credits never expire - use them at your own pace</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Check className="w-5 h-5 text-indigo-600 mt-0.5" />
            <div>
              <p className="font-semibold text-gray-900">Instant Delivery</p>
              <p className="text-sm text-gray-600">Credits added to your account immediately after purchase</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Check className="w-5 h-5 text-indigo-600 mt-0.5" />
            <div>
              <p className="font-semibold text-gray-900">Cancel Anytime</p>
              <p className="text-sm text-gray-600">No subscriptions or commitments - buy credits as needed</p>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="mt-12">
        <h3 className="text-2xl font-bold text-center text-gray-900 mb-8">Frequently Asked Questions</h3>
        <div className="space-y-4">
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <h4 className="font-semibold text-gray-900 mb-2">How do credits work?</h4>
            <p className="text-gray-600">Each invoice you process uses 1 credit. Credits never expire and can be used anytime.</p>
          </div>
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <h4 className="font-semibold text-gray-900 mb-2">What payment methods do you accept?</h4>
            <p className="text-gray-600">We accept all major credit cards through Stripe's secure payment processing.</p>
          </div>
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <h4 className="font-semibold text-gray-900 mb-2">Can I get a refund?</h4>
            <p className="text-gray-600">Yes! If you're not satisfied within the first 7 days, we offer a full refund on unused credits.</p>
          </div>
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <h4 className="font-semibold text-gray-900 mb-2">Do you offer volume discounts?</h4>
            <p className="text-gray-600">Yes! The more credits you purchase, the lower the per-invoice cost. Contact us for enterprise pricing.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;